import { BentoCard, RydeFade, RydeFooter, RydeNav, SectionTag } from "@/components/ryde/Ryde";

const PageShell = ({ eyebrow, title, children, testId }) => (
  <main className="min-h-screen bg-[#0B0D0C] font-body text-[#F4F7F5]" data-testid={testId}>
    <RydeNav minimal />
    <section className="px-5 py-32 md:px-8 md:py-40">
      <RydeFade className="mx-auto max-w-3xl">
        <SectionTag>{eyebrow}</SectionTag>
        <h1 className="mt-4 font-display text-4xl font-black tracking-[-0.02em] md:text-6xl">{title}</h1>
        <div className="mt-10 space-y-5">{children}</div>
      </RydeFade>
    </section>
    <RydeFooter />
  </main>
);

const Section = ({ title, children, testId }) => (
  <BentoCard data-testid={testId}>
    <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#C6FF3D]">{title}</h2>
    <div className="mt-4 space-y-3 text-sm leading-7 text-[#9AA8A0]">{children}</div>
  </BentoCard>
);

export function RydePrivacyPage() {
  return (
    <PageShell eyebrow="Privacy" title="What we collect and why." testId="privacy-page">
      <Section title="Booking details" testId="privacy-booking">
        <p>We collect your name, WhatsApp number, city, and optional note to create your Razorpay order, hold your queue seat, contact you about your batch, and deliver your Slate 01.</p>
      </Section>
      <Section title="Payment information" testId="privacy-payments">
        <p>Payments are processed by Razorpay. RydeCast does not store your card number, UPI PIN, or banking password. We store booking status, Razorpay order/payment identifiers, and your queue ID.</p>
      </Section>
      <Section title="Website analytics" testId="privacy-analytics">
        <p>We use first-party analytics with a random session ID to understand page visits, scroll depth, section views, CTA clicks, form starts, and payment milestones. We do not send your form values to analytics events.</p>
      </Section>
      <Section title="Support messages" testId="privacy-support">
        <p>If you contact us on WhatsApp or through support, we store the name, phone number, and message you submit so the team can respond.</p>
      </Section>
    </PageShell>
  );
}

export function RydeRefundsPage() {
  return (
    <PageShell eyebrow="Refunds" title="Clear refund terms. No fine print games." testId="refunds-page">
      <Section title="Until dispatch week is sent" testId="refund-before-dispatch">
        <p>All money paid — the ₹499 booking or the full ₹4,999 — is fully refundable until we send you your dispatch week with photo and serial number. Cancel anytime before that, no questions asked.</p>
      </Section>
      <Section title="Failed or unverified payments" testId="refund-failed-payment">
        <p>If Razorpay shows money deducted but RydeCast does not confirm your booking, WhatsApp us with your phone number and payment reference. We verify before confirming or refunding.</p>
      </Section>
      <Section title="After dispatch" testId="refund-after-dispatch">
        <p>Once the dispatch week is sent and the unit ships, the order moves to the hardware warranty: 1-year exchange for malfunction instead of refund. You keep the tablet — it works as a normal Android tablet regardless of ads.</p>
      </Section>
      <Section title="Refund route" testId="refund-route">
        <p>Approved refunds are returned to the original payment method where possible. Bank/UPI posting time can vary by provider.</p>
      </Section>
    </PageShell>
  );
}

export function RydeTermsPage() {
  return (
    <PageShell eyebrow="Terms" title="Batch terms in plain language." testId="terms-page">
      <Section title="The product" testId="terms-product">
        <p>Slate 01 is a 10.1" Android tablet (not Apple, not an iPad) with the RydeCast player preloaded. The ₹4,999 kit includes the tablet, inside rear-window mount, car power lead, RydeCast software, and 1-year hardware exchange.</p>
      </Section>
      <Section title="Booking & delivery" testId="terms-booking">
        <p>₹499 locks your unit and is refundable until dispatch. Delivery is 15–20 days from booking; we share your unit’s photo, serial number, and tracking on dispatch. The ₹4,500 balance is prepaid or COD at your door.</p>
      </Section>
      <Section title="Ad earnings" testId="terms-earnings">
        <p>Earnings come only from verified playtime on live brand campaigns in your city, paid on the 5th by UPI or bank transfer when ads actually ran. No campaign means no payout that month. Earnings are never a fixed salary or guarantee.</p>
      </Section>
      <Section title="Fitment and visibility" testId="terms-fitment">
        <p>Slate 01 mounts on the interior side of the rear windshield with a removable no-drill mount. Keep practical rear visibility and follow applicable motor-vehicle rules. RydeCast does not claim blanket government approval; local enforcement can vary.</p>
      </Section>
      <Section title="Owner responsibilities" testId="terms-owner">
        <p>Provide accurate information, avoid tampering or unofficial ROMs, keep the unit secure, and follow traffic laws while it is mounted.</p>
      </Section>
      <Section title="Contact" testId="terms-contact">
        <p>Reach us on WhatsApp at +91 80063 31010 for booking, refund, payment, and fitment questions.</p>
      </Section>
    </PageShell>
  );
}
